/**
 * Admin TTS moderation page (Slice 2 CP13 Phase 1).
 *
 * Brutally minimal per the CP13 first-slice plan §IN/OUT:
 *   • read-only paginated list of every `tts_entries` row, regardless of
 *     review_state (so an operator can flip a mistakenly-approved row
 *     back to `'flagged'` without first re-finding it);
 *   • inline four-button review-state cell on every row, mirroring the
 *     CP11 pattern in Events.tsx (`approved` / `hidden` / `flagged` /
 *     `unreviewed`); hide + flag prompt for an optional reason via
 *     `window.prompt`, approve + unreview never prompt;
 *   • simple prev / next pagination (no jump-to-page, no filter row);
 *   • no create / edit affordance — TTS rows are written by the live
 *     ingestion path and the public archive jobs, not by humans.
 *
 * The 4-button group + prompt-for-reason flow is intentionally a
 * one-for-one copy of `Events.tsx`'s `ReviewStateCell`. The two
 * surfaces share the same operator vocabulary; keeping them visually
 * identical means an editor who has used the Events page can use this
 * one with zero ramp. The cell is renamed `TtsReviewStateCell` and the
 * narrowing switch is rebound to `TtsEntryReviewStateValidationError`
 * (the TTS variant uses the table-namespaced `tts_*` codes for
 * `_unchanged`, `_idempotency_conflict`, and `_entry_not_found`).
 *
 * Idempotency: each click generates a fresh `crypto.randomUUID()`. A
 * retry of the same flip after a network blip will replay on the
 * Worker's `mutation_events.idempotency_key` UNIQUE → the prior
 * receipt is returned and no second audit row is written. The TTS
 * Worker also writes through to `tts_entries.hide_reason` per the
 * CP13 rule (set on hide / flag, cleared on approve / unreview); this
 * page only reads back the post-write `review_state` from the
 * invalidated query.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  RecapReviewState,
  TtsAdminQueueItem,
  TtsEntryDeleteReceipt,
  TtsEntryReviewStateValidationError,
} from '@fishstank/contracts'
import {
  deleteTtsEntry,
  fetchTtsAdminQueue,
  patchTtsEntryReviewState,
  type TtsEntryDeleteFetchResult,
} from '../lib/worker'
import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'

// crypto.randomUUID is widely available in modern browsers + jsdom;
// matches the wrap in Events.tsx so the test harness can swap it.
function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

const BODY_PREVIEW_MAX = 140

function previewBody(body: string): string {
  if (body.length <= BODY_PREVIEW_MAX) return body
  return `${body.slice(0, BODY_PREVIEW_MAX - 1).trimEnd()}…`
}

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export function TtsPage() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['tts', 'admin', 'queue', { page }],
    queryFn: () => fetchTtsAdminQueue({ page }),
  })

  const result = query.data

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['tts', 'admin', 'queue'] })

  return (
    <Shell
      provenance={
        result && (result.kind === 'ok' || result.kind === 'empty')
          ? result.artifact.provenance
          : null
      }
    >
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">TTS</h1>
          <p className="mt-1 text-sm text-slate-400">
            Admin moderation queue. Every{' '}
            <code className="text-slate-300">review_state</code> surfaces
            here. Use the inline buttons to flip a row's state — hide /
            flag prompt for an optional reason; approve / unreview do
            not.
          </p>
        </div>
      </header>

      <FetchedArtifact
        result={result}
        isLoading={query.isLoading}
        testidPrefix="tts"
        labels={{
          loading: 'Loading TTS entries…',
          errorTitle: "Couldn't load TTS entries.",
          empty: 'No TTS entries on this page.',
        }}
      >
        {(artifact) => (
          <>
            <AdminTtsTable
              items={artifact.body.items}
              onChanged={invalidate}
            />
            <Pagination
              page={artifact.body.pagination.page}
              hasNext={artifact.body.pagination.has_next}
              totalReturned={artifact.body.pagination.total_returned}
              onChange={setPage}
            />
          </>
        )}
      </FetchedArtifact>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Table
// ────────────────────────────────────────────────────────────────────────────

function AdminTtsTable({
  items,
  onChanged,
}: {
  items: readonly TtsAdminQueueItem[]
  onChanged: () => void
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="tts-table"
        data-tts-count={items.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-20">ID</th>
            <th className="px-3 py-2 text-left font-medium">Body</th>
            <th className="px-3 py-2 text-left font-medium w-28">Voice</th>
            <th className="px-3 py-2 text-left font-medium w-44">Spoken at</th>
            <th className="px-3 py-2 text-left font-medium w-24">Source</th>
            <th className="px-3 py-2 text-left font-medium w-28">Arc</th>
            <th className="px-3 py-2 text-left font-medium w-40">Entities</th>
            <th className="px-3 py-2 text-left font-medium w-44">State</th>
            <th className="px-3 py-2 text-right font-medium w-32">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {items.map((row) => (
            <AdminTtsRow key={row.id} row={row} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AdminTtsRow({
  row,
  onChanged,
}: {
  row: TtsAdminQueueItem
  onChanged: () => void
}) {
  return (
    <tr
      data-testid="tts-row"
      data-tts-id={row.id}
      data-review-state={row.review_state}
      className="hover:bg-slate-900/40"
    >
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.id}
      </td>
      <td className="px-3 py-1.5 text-slate-100">
        <span data-testid="tts-row-body">{previewBody(row.body)}</span>
        {row.hide_reason && (
          <div
            data-testid="tts-row-hide-reason"
            className="mt-1 text-[10px] uppercase tracking-wider text-slate-500"
          >
            reason: <span className="text-slate-400">{row.hide_reason}</span>
          </div>
        )}
      </td>
      <td className="px-3 py-1.5 text-slate-300 text-xs">
        {row.voice ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.spoken_at ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5 text-slate-300 text-xs">{row.tts_source}</td>
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.arc?.slug ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5 text-slate-300 text-xs">
        {row.entities.length === 0 ? (
          <span className="text-slate-600">—</span>
        ) : (
          row.entities.map((e) => e.display_name).join(', ')
        )}
      </td>
      <td className="px-3 py-1.5">
        <TtsReviewStateCell row={row} onChanged={onChanged} />
      </td>
      <td className="px-3 py-1.5 text-right">
        <TtsDeleteControl row={row} onDeleted={onChanged} />
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Delete control (slice 4)
//
// Inline confirm pattern lifted from the slice 3 Events.tsx
// `EventDeleteControl` — single click opens an inline Confirm/Cancel
// pair, second click submits the DELETE. One crypto.randomUUID() per
// confirm-open so a double-click of Confirm replays the same audit
// row instead of writing a second one. Children
// (`tts_entry_entities` rows) cascade at the DB level.
//
// On 404 (tts_entry_not_found) we treat the row as already gone:
// invalidate the queue query and clear local state. The DB is the
// authority either way.
// ────────────────────────────────────────────────────────────────────────────

function TtsDeleteControl({
  row,
  onDeleted,
}: {
  row: TtsAdminQueueItem
  onDeleted: () => void
}) {
  const queryClient = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [networkError, setNetworkError] = useState<string | null>(null)
  // Pinned per confirm-open lifecycle. Rotates on each new open.
  const idempotencyKey = useMemo(
    () => globalThis.crypto.randomUUID(),
    [row.id, confirmOpen],
  )

  const mutation = useMutation<
    TtsEntryDeleteFetchResult<TtsEntryDeleteReceipt>,
    unknown,
    void
  >({
    mutationFn: () =>
      deleteTtsEntry(row.id, {
        body: { idempotency_key: idempotencyKey },
      }),
    onSuccess: (res) => {
      setNetworkError(null)
      if (res.kind === 'ok') {
        queryClient.invalidateQueries({
          queryKey: ['tts', 'admin', 'queue'],
        })
        setConfirmOpen(false)
        onDeleted()
      } else if (res.kind === 'not_found') {
        // Treat as already deleted (probably another tab beat us).
        queryClient.invalidateQueries({
          queryKey: ['tts', 'admin', 'queue'],
        })
        setConfirmOpen(false)
        onDeleted()
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'validation_error') {
        if (res.error.code === 'tts_entry_not_found') {
          queryClient.invalidateQueries({
            queryKey: ['tts', 'admin', 'queue'],
          })
          setConfirmOpen(false)
          onDeleted()
          return
        }
        setNetworkError(`Validation: ${res.error.code}`)
      } else {
        setNetworkError(res.message)
      }
    },
  })

  if (!confirmOpen) {
    return (
      <button
        type="button"
        data-testid="tts-delete-open"
        data-tts-id={row.id}
        onClick={() => {
          setNetworkError(null)
          setConfirmOpen(true)
        }}
        className="rounded-sm border border-rose-700 bg-rose-950/20 px-2 py-0.5 text-xs text-rose-200 hover:bg-rose-900/40"
      >
        Delete
      </button>
    )
  }

  return (
    <span
      data-testid="tts-delete-confirm"
      data-tts-id={row.id}
      className="inline-flex items-center gap-1"
    >
      <button
        type="button"
        data-testid="tts-delete-confirm-yes"
        data-tts-id={row.id}
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="rounded-sm bg-rose-700 px-2 py-0.5 text-xs font-semibold text-white disabled:opacity-50"
      >
        {mutation.isPending ? 'Deleting…' : 'Confirm delete'}
      </button>
      <button
        type="button"
        data-testid="tts-delete-confirm-cancel"
        data-tts-id={row.id}
        onClick={() => {
          setConfirmOpen(false)
          setNetworkError(null)
        }}
        disabled={mutation.isPending}
        className="rounded-sm border border-slate-700 px-2 py-0.5 text-xs text-slate-200"
      >
        Cancel
      </button>
      {networkError && (
        <span
          data-testid="tts-delete-error"
          className="ml-2 text-[10px] text-rose-300"
        >
          {networkError}
        </span>
      )}
    </span>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Review-state cell
//
// Mirrors `ReviewStateCell` in Events.tsx (CP11 first slice). The
// only deltas:
//   - calls patchTtsEntryReviewState instead of patchRecapEventReviewState
//   - narrows on TtsEntryReviewStateValidationError (table-namespaced
//     `tts_*` codes)
//   - testid prefix is `tts-review-state-*`
// ────────────────────────────────────────────────────────────────────────────

const REVIEW_STATE_BUTTONS: ReadonlyArray<{
  state: RecapReviewState
  label: string
  className: string
  promptForReason: boolean
}> = [
  {
    state: 'approved',
    label: 'Approve',
    className:
      'rounded-sm border border-emerald-700 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-30',
    promptForReason: false,
  },
  {
    state: 'hidden',
    label: 'Hide',
    className:
      'rounded-sm border border-slate-600 bg-slate-900 text-slate-200 hover:bg-slate-800 disabled:opacity-30',
    promptForReason: true,
  },
  {
    state: 'flagged',
    label: 'Flag',
    className:
      'rounded-sm border border-amber-700 bg-amber-950/30 text-amber-200 hover:bg-amber-900/40 disabled:opacity-30',
    promptForReason: true,
  },
  {
    state: 'unreviewed',
    label: 'Unreview',
    className:
      'rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30',
    promptForReason: false,
  },
]

function ttsReviewValidationMessage(
  err: TtsEntryReviewStateValidationError,
): string {
  switch (err.code) {
    case 'invalid_review_state':
      return `Server rejected to_state="${err.received}".`
    case 'tts_review_state_unchanged':
      return `Already at ${err.current}.`
    case 'tts_idempotency_conflict':
      return `Conflict: that key already flipped to ${err.prior_to_state} (you sent ${err.requested_to_state}). Retrying with a fresh key.`
    case 'invalid_uuid':
      return 'Internal error: idempotency key invalid. Refresh and try again.'
    case 'invalid_enum':
      return `Invalid value for "${err.field}".`
    case 'tts_entry_not_found':
      return 'This TTS entry no longer exists.'
    default:
      return 'Review-state flip failed.'
  }
}

function TtsReviewStateCell({
  row,
  onChanged,
}: {
  row: TtsAdminQueueItem
  onChanged: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<RecapReviewState | null>(null)

  async function flip(to: RecapReviewState, promptForReason: boolean) {
    if (row.review_state === to) return
    let reason: string | null = null
    if (promptForReason) {
      // window.prompt is the deliberate "minimal UI" choice for the
      // first slice — see Events.tsx's ReviewStateCell module comment.
      const supplied = window.prompt(
        `Optional reason for flipping #${row.id} to ${to} (leave blank to skip):`,
        '',
      )
      if (supplied === null) return // operator hit Cancel
      const trimmed = supplied.trim()
      reason = trimmed.length > 0 ? trimmed : null
    }

    setError(null)
    setPending(to)
    try {
      const res = await patchTtsEntryReviewState(row.id, {
        body: {
          idempotency_key: newIdempotencyKey(),
          to_state: to,
          reason,
        },
      })
      if (res.kind === 'ok') {
        onChanged()
        return
      }
      if (res.kind === 'validation_error') {
        setError(ttsReviewValidationMessage(res.error))
        return
      }
      if (res.kind === 'csrf_error') {
        setError(`CSRF check failed: ${res.reason}`)
        return
      }
      if (res.kind === 'not_found') {
        setError('This TTS entry no longer exists.')
        return
      }
      setError(res.message)
    } finally {
      setPending(null)
    }
  }

  return (
    <div
      data-testid="tts-review-state-cell"
      data-tts-id={row.id}
      data-review-state={row.review_state}
      className="flex flex-col gap-1"
    >
      <span className="text-xs text-slate-300 uppercase">{row.review_state}</span>
      <div className="flex flex-wrap gap-1">
        {REVIEW_STATE_BUTTONS.map((b) => {
          const disabled = row.review_state === b.state || pending !== null
          return (
            <button
              key={b.state}
              type="button"
              data-testid="tts-review-state-button"
              data-tts-id={row.id}
              data-to-state={b.state}
              disabled={disabled}
              onClick={() => {
                void flip(b.state, b.promptForReason)
              }}
              className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${b.className}`}
            >
              {pending === b.state ? '…' : b.label}
            </button>
          )
        })}
      </div>
      {error && (
        <span
          data-testid="tts-review-state-error"
          className="text-[10px] text-red-300"
        >
          {error}
        </span>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Pagination
//
// Worker returns `page`, `has_next`, `total_returned`. We render prev /
// next disabled at the boundaries. No jump-to-page in Phase 1.
// ────────────────────────────────────────────────────────────────────────────

function Pagination({
  page,
  hasNext,
  totalReturned,
  onChange,
}: {
  page: number
  hasNext: boolean
  totalReturned: number
  onChange: (page: number) => void
}) {
  const prevDisabled = page <= 1
  const nextDisabled = !hasNext
  return (
    <div
      data-testid="tts-pagination"
      data-page={page}
      data-has-next={hasNext}
      className="mt-4 flex items-center justify-between text-xs text-slate-400"
    >
      <span>
        Page {page} · {totalReturned} row{totalReturned === 1 ? '' : 's'}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="tts-pagination-prev"
          disabled={prevDisabled}
          onClick={() => onChange(page - 1)}
          className="rounded-sm border border-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-800 disabled:opacity-30"
        >
          ← Prev
        </button>
        <button
          type="button"
          data-testid="tts-pagination-next"
          disabled={nextDisabled}
          onClick={() => onChange(page + 1)}
          className="rounded-sm border border-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-800 disabled:opacity-30"
        >
          Next →
        </button>
      </div>
    </div>
  )
}
